import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../services/api-client';
import type {
  CreateExportPayload,
  CreateExportResponse,
  ExportStatus,
  ExportSummary,
  PageEnvelope,
} from '../types/api';

export const exportKeys = {
  all: ['exports'] as const,
  list: () => [...exportKeys.all, 'list'] as const,
  detail: (id: string) => [...exportKeys.all, 'detail', id] as const,
};

/** Export não tem `PARTIAL` nem `CANCELLED` — o vocabulário não é o do job. */
const TERMINAL_STATUSES: readonly ExportStatus[] = ['COMPLETED', 'FAILED', 'EXPIRED'];

export function isTerminalExportStatus(status: ExportStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status);
}

export const EXPORT_POLL_INTERVAL_MS = 2000;

export function useCreateExport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateExportPayload) =>
      apiRequest<CreateExportResponse>('/exports', { method: 'POST', body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: exportKeys.list() });
    },
  });
}

/** Acompanha uma exportação até o estado terminal, e então para (RNF-EU04). */
export function useExportDetail(exportId: string | undefined) {
  return useQuery({
    queryKey: exportKeys.detail(exportId ?? ''),
    enabled: Boolean(exportId),
    queryFn: () => apiRequest<ExportSummary>(`/exports/${exportId}`),
    refetchInterval: (query) =>
      isTerminalExportStatus(query.state.data?.status) ? false : EXPORT_POLL_INTERVAL_MS,
  });
}

export function useExportList(limit = 20) {
  return useInfiniteQuery({
    queryKey: exportKeys.list(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiRequest<PageEnvelope<ExportSummary>>('/exports', {
        query: { cursor: pageParam ?? undefined, limit },
      }),
    getNextPageParam: (lastPage) => lastPage.page.nextCursor,
    // A lista só consulta enquanto houver algo sendo gerado (RF-EU14): um
    // histórico inteiramente terminal não muda sozinho.
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const generating = pages.some((page) =>
        page.data.some((item) => !isTerminalExportStatus(item.status)),
      );
      return generating ? EXPORT_POLL_INTERVAL_MS : false;
    },
  });
}
